"use client";

import { c as _c } from "react/compiler-runtime";
import { SelfScheduleCard } from "../self-schedule/components/self-schedule-card";
import React from "react";
import { FaArrowLeft, FaStore, FaTruck } from "react-icons/fa";
import { Button } from "@/components/ui";
import { formatCurrency } from "@/lib/format-data";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import type { ShopShippingPolicy } from "@/types/shop";
import { shippingFeeFor } from "./cart-line-utils";
interface FulfillmentMethodStepProps {
  shipping: ShopShippingPolicy;
  shippableSubtotalCents: number;
  selectedMethod: ShopFulfillmentMethod | null;
  pickupAvailable: boolean;
  onSelectMethod: (method: ShopFulfillmentMethod) => void;
  onBack: () => void;
}
const optionIconClassName = "h-12 w-12 flex-shrink-0 px-3 text-primary/80";
const shippingCostLabel = (shipping: ShopShippingPolicy, shippableSubtotalCents: number): string => {
  const feeCents = shippingFeeFor(shipping, shippableSubtotalCents);
  if (feeCents === 0) return "Free shipping";
  if (shipping.freeShippingOverCents === null) {
    return `+${formatCurrency(feeCents)} shipping`;
  }
  return `+${formatCurrency(feeCents)} shipping · free over ${formatCurrency(shipping.freeShippingOverCents)}`;
};

/** Lets the patient skip the shipping fee by collecting ship-eligible items at a clinic. */
const FulfillmentMethodStep: React.FC<FulfillmentMethodStepProps> = t0 => {
  const $ = _c(33);
  const {
    shipping,
    shippableSubtotalCents,
    selectedMethod,
    pickupAvailable,
    onSelectMethod,
    onBack
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FaArrowLeft className="mr-2 text-xxs" />;
    t2 = <span>Back</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onBack) {
    t3 = <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 cursor-pointer px-0 text-foreground-secondary">{t1}{t2}</Button>;
    $[2] = onBack;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-start gap-3"><div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-background-secondary text-primary"><FaTruck className="h-4 w-4" /></div><div><p className="text-overline text-primary">Delivery</p><h2 className="mt-1 text-h3 text-foreground">How would you like your order?</h2><p className="mt-2 text-body text-foreground-secondary">Choose shipping or pick it up at the clinic.</p></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== shippableSubtotalCents || $[6] !== shipping) {
    t5 = shippingCostLabel(shipping, shippableSubtotalCents);
    $[5] = shippableSubtotalCents;
    $[6] = shipping;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FaTruck className={optionIconClassName} />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = {
      id: ShopFulfillmentMethod.SHIP,
      name: "Ship to me",
      address: t5,
      type: "location",
      isPopular: false,
      image: t6
    };
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = selectedMethod === ShopFulfillmentMethod.SHIP;
  let t9;
  if ($[11] !== onSelectMethod) {
    t9 = () => onSelectMethod(ShopFulfillmentMethod.SHIP);
    $[11] = onSelectMethod;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t10 = <SelfScheduleCard item={t7} isSelected={t8} onItemClick={t9} hideInfoButton={true} className="min-h-0 overflow-visible p-4 px-5 !pb-4" />;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      id: ShopFulfillmentMethod.PICKUP,
      name: "Pick up at the clinic",
      address: "Pick up at the clinic at no shipping cost",
      type: "location",
      isPopular: false,
      image: <FaStore className={optionIconClassName} />
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = selectedMethod === ShopFulfillmentMethod.PICKUP;
  const t13 = !pickupAvailable;
  let t14;
  if ($[18] !== onSelectMethod) {
    t14 = () => onSelectMethod(ShopFulfillmentMethod.PICKUP);
    $[18] = onSelectMethod;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t12 || $[21] !== t13 || $[22] !== t14) {
    t15 = <SelfScheduleCard item={t11} isSelected={t12} disabled={t13} onItemClick={t14} hideInfoButton={true} className="min-h-0 overflow-visible p-4 px-5 !pb-4" />;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t10 || $[25] !== t15) {
    t16 = <div role="group" aria-label="Delivery method" className="mt-6 grid grid-cols-1 gap-4">{t10}{t15}</div>;
    $[24] = t10;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="mt-6 text-caption text-foreground-secondary">Choosing an option takes you to the next step.</p>;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16) {
    t18 = <section className="py-2">{t4}{t16}{t17}</section>;
    $[28] = t16;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t18 || $[31] !== t3) {
    t19 = <div className="motion-preset-slide-right-sm mx-auto max-w-xl">{t3}{t18}</div>;
    $[30] = t18;
    $[31] = t3;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  return t19;
};
export default FulfillmentMethodStep;